import React, { useEffect, useRef, useCallback } from "react";

const HeroBackground = () => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const particlesRef = useRef([]);
  const geometriesRef = useRef([]);
  const rafRef = useRef(null);

  const initParticles = useCallback((width, height) => {
    const isMobile = width < 768;
    const count = isMobile ? 80 : 180;
    const particles = [];

    for (let i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        baseX: Math.random() * width,
        baseY: Math.random() * height,
        size: Math.random() * 2 + 0.5,
        opacity: Math.random() * 0.4 + 0.1,
        speedX: (Math.random() - 0.5) * 0.3,
        speedY: (Math.random() - 0.5) * 0.3,
        // Gold color variation
        color: ['#D4A843', '#E8C260', '#C8962A', '#F0D070'][Math.floor(Math.random() * 4)],
      });
    }
    particlesRef.current = particles;

    // Floating wireframe geometries
    const geoCount = isMobile ? 4 : 8;
    const geos = [];
    for (let i = 0; i < geoCount; i++) {
      const sides = [3, 4, 5, 6][Math.floor(Math.random() * 4)];
      geos.push({
        x: Math.random() * width,
        y: Math.random() * height,
        size: 20 + Math.random() * 40,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.008,
        floatOffset: Math.random() * Math.PI * 2,
        floatSpeed: 0.3 + Math.random() * 0.5,
        floatAmount: 15 + Math.random() * 20,
        sides,
        opacity: 0.06 + Math.random() * 0.08,
        color: ['#D4A843', '#E8C260', '#C8962A'][Math.floor(Math.random() * 3)],
      });
    }
    geometriesRef.current = geos;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initParticles(canvas.width, canvas.height);
    };
    resize();
    window.addEventListener("resize", resize);

    let time = 0;

    const animate = () => {
      time += 0.016;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;

      // Draw floating geometries
      geometriesRef.current.forEach((geo) => {
        geo.rotation += geo.rotationSpeed;
        const floatY = Math.sin(time * geo.floatSpeed + geo.floatOffset) * geo.floatAmount;

        ctx.save();
        ctx.translate(geo.x, geo.y + floatY);
        ctx.rotate(geo.rotation);
        ctx.beginPath();

        for (let i = 0; i <= geo.sides; i++) {
          const angle = (i / geo.sides) * Math.PI * 2;
          const px = Math.cos(angle) * geo.size;
          const py = Math.sin(angle) * geo.size;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }

        ctx.closePath();
        ctx.strokeStyle = geo.color;
        ctx.globalAlpha = geo.opacity;
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.restore();
      });

      // Draw and update particles
      particlesRef.current.forEach((p) => {
        // Drift movement
        p.x += p.speedX;
        p.y += p.speedY;

        // Wrap around edges
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;
        if (p.y < -10) p.y = canvas.height + 10;
        if (p.y > canvas.height + 10) p.y = -10;

        // Mouse interaction - subtle push
        const dx = p.x - mx;
        const dy = p.y - my;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 150 && dist > 0) {
          const force = (150 - dist) / 150;
          p.x += (dx / dist) * force * 1.5;
          p.y += (dy / dist) * force * 1.5;
        }

        // Draw particle
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.opacity;
        ctx.fill();
      });

      // Draw connections between nearby particles
      ctx.lineWidth = 0.5;
      for (let i = 0; i < particlesRef.current.length; i++) {
        for (let j = i + 1; j < particlesRef.current.length; j++) {
          const a = particlesRef.current[i];
          const b = particlesRef.current[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.strokeStyle = "#D4A843";
            ctx.globalAlpha = (1 - dist / 120) * 0.06;
            ctx.stroke();
          }
        }
      }

      // Central glow orb
      const glowSize = 200 + Math.sin(time * 0.5) * 30;
      const gradient = ctx.createRadialGradient(
        canvas.width / 2, canvas.height / 2, 0,
        canvas.width / 2, canvas.height / 2, glowSize
      );
      gradient.addColorStop(0, "rgba(212, 168, 67, 0.03)");
      gradient.addColorStop(1, "rgba(212, 168, 67, 0)");
      ctx.globalAlpha = 1;
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("resize", resize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [initParticles]);

  return (
    <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
      {/* Mouse tracking layer */}
      <div
        className="absolute inset-0 z-10"
        style={{ pointerEvents: "auto" }}
        onMouseMove={(e) => {
          mouseRef.current = { x: e.clientX, y: e.clientY };
        }}
      />
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
      />
    </div>
  );
};

export default HeroBackground;
